import { ReactNode } from 'react'
import { createTranslator, Locale } from 'next-intl'
import { deepMerge } from '@reward-platform/utils'
import { Iata, MessageCluster } from '@reward-platform/shared-schemas'
import { getMessageClusters } from '~/services/contentService'
import { ONE_HOUR } from '~/utils/datetime'
import { CachedMessageClusterValue, Messages } from './localisation.types'

const CACHE_EXPIRATION_TIME = ONE_HOUR * 2

const TO_TAG = {
  OPEN: '<to>',
  CLOSE: '</to>',
}

export function replaceToTag(link: string | ReactNode[]): JSX.Element {
  const target = link.toString()

  const betweenTo = /(<to>)(.*)(<\/to>)/g
  const matches = target.match(betweenTo)

  if (matches === null || matches.length !== 1) {
    throw new Error('Link tags should have exactly one `to` tag')
  }

  const rawHref = matches[0]
  const linkText = target.replace(rawHref, '')

  const href = rawHref.replace(TO_TAG.OPEN, '').replace(TO_TAG.CLOSE, '')

  return <a href={href}>{linkText}</a>
}

export const renderHtmlTagMap = {
  bold: (...chunks: any) => <strong>{chunks}</strong>,
  /** Supports usage of Links
      <link><to>/somewhere/cool</to>Somewhere Cool</link>
                              \/
      <a href="/somewhere/cool">Somewhere Cool</a>
  */
  link: (...link: any) => replaceToTag(link),
  /** the <to> tag here is kept in here because otherwise legacy @formatjs will throw a formatting error. But we still need it because it is used in the context of the<link> tag above. Can find a better link solution using next-intl as we migrate
   */
  to: (...to: any) => `<to>${to}</to>`,
  p: (...chunks: any) => <p>{chunks}</p>,
  br: () => <br />,
}

export function mergeMessagesWithDefaults(
  messages: MessageCluster,
  defaultMessages: MessageCluster
) {
  return deepMerge({ ...defaultMessages }, { ...messages })
}

// Formats the default message using the html tag map and any injected variables
// used to cover any messages missing from the json files
export function formatRichMessageForKey(
  key: string,
  messages: MessageCluster,
  values?: Record<string, any>,
  locale: Locale = 'en-US'
): string {
  const t = createTranslator({
    messages,
    locale,
  })
  return t.rich(key as never, { ...renderHtmlTagMap, ...values } as unknown as undefined) as string
}

// Save a cluster into local storage, with a timestamp for managing cache expiry
export function saveClusterInCache(key: string, locale: string, messages: Messages) {
  localStorage.setItem(
    `cluster_${key}_${locale}`,
    JSON.stringify({ lastUpdatedAt: Date.now(), messages })
  )
}

// Read a cluster from the cache and parse it as the correct type
export function getClusterFromCache(
  key: string,
  locale: string
): CachedMessageClusterValue | undefined {
  const storageKey = `cluster_${key}_${locale}`
  const items = localStorage.getItem(storageKey)

  return items ? (JSON.parse(items) as CachedMessageClusterValue) : undefined
}

// Retrieve a cluster dynamically
// Loads from cache if possible, if uncached or stale, retrieves from API
export async function getCluster(opCo: Iata, key: string, locale: string) {
  let messagesFromCluster: Messages

  // Read from cache if possible
  const cached = getClusterFromCache(key, locale)

  // Check if cached message exists and is not stale
  if (cached && cached.lastUpdatedAt > Date.now() - CACHE_EXPIRATION_TIME) {
    messagesFromCluster = cached.messages
  } else {
    // Retrieve fresh message cluster from data source
    const cluster = await getMessageClusters(opCo, [key], locale)
    messagesFromCluster = cluster[key]

    // Save cluster in cache
    saveClusterInCache(key, locale, messagesFromCluster)
  }

  return messagesFromCluster
}
